/**
 * @module Service
 */

import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import {
	Order,
	OrderLineItemGroup,
	OrderImport,
	OrderImportDetails,
	OrderLineItemService,
	OrderService, VendorLineItem, NON_UNION_ORDER_TEMPLATE_ID,
} from '@feature/orders/shared';
import { OrderUser, OrderUserPair, UserContextRef, UserPair, UserService } from '@feature/users/shared';
import { Company, CompanyPair, CompanyService } from '@feature/companies/shared';
import { AppContextService } from '@app/services/app-context.service';
import { CountryService } from '@app/services/country.service';
import { State, CANADA_COUNTRY_ID, USA_COUNTRY_ID } from '@app/models/country.model';
import {
	ACTRA_NATURE_OF_PRODUCTION,
	ACTRA_PERFORMANCE_CATEGORY,
	PAYER_SIGNATORY_OPTIONS,
	PAYMENT_SIGNATORY,
	PLACE_OF_PERFORMANCE,
	CustomField,
	CustomFieldChoice,
	CustomFieldValue,
} from '@app/components/custom-fields/shared/custom-field.model';
import { CustomFieldService } from '@app/components/custom-fields/shared/custom-field.service';
import { AppContext } from '@app/models/app-context.model';
import { filter, map, switchMap } from 'rxjs/operators';
import { Document } from '@feature/document-center/shared/document.model';
import { UtilsService } from '@app/services/utils.service';

/**
 * Service for managing import order related operations within the application.
 *
 * This service provides order import functionality, such as fetching of orders, validation,
 * calculation and conversion of imported data into operational orders.
 *
 * @service
 * @@Injectable
 */
@Injectable({ providedIn: 'root' })
export class OrderImportService {
	/**
	 * Represents the application context for managing user or company-related data.
	 *
	 * This property holds the current application context, which includes information about
	 * the authenticated user and their associated company membership.
	 *
	 * @type {AppContext}
	 */
	public ctx: AppContext;

	/**
	 * The source document associated with the current input operation.
	 *
	 * Normally, this property holds the input CSV document that is saved for log purposes.
	 *
	 * @type {Document}
	 */
	public inputDocument: Document;

	/**
	 * BehaviorSubject that holds the details of the import orders.
	 *
	 * This private property is used to manage the state of import order details and allows
	 * subscribers to reactively receive updates whenever the import order details change.
	 *
	 * @type {BehaviorSubject<Array<OrderImportDetails>>}
	 * @private
	 * @hidden
	 */
	private readonly _orderImportDetails$: BehaviorSubject<Array<OrderImportDetails>>;

	/**
	 * Represents the payer's context reference.
	 *
	 * This property holds the UserContextRef object that contains information about the payer,
	 * including their ID and profile details.
	 *
	 * @type {UserContextRef}
	 */
	public payer: UserContextRef;

	/**
	 * An array of states available for the selected country.
	 * @type {Array<State>}
	 */
	public states: Array<State>;

	/**
	 * An array of custom fields associated with the current talent pair.
	 * @type {Array<CustomField>}
	 */
	public customFields: Array<CustomField>;

	/**
	 * Getter that returns an observable that emits the total count of valid orders.
	 *
	 * @getter
	 * @returns {Observable<number>}
	 */
	get ordersImportDetailsValidCount$(): Observable<number> {
		return this._orderImportDetails$.pipe(
			// Filter out OrderImportDetails objects with empty validation errors.
			filter((detailsArray: Array<OrderImportDetails>) => detailsArray?.length > 0),
			map((detailsArray: Array<OrderImportDetails>) => {
				return detailsArray.reduce((totalCount: number, details: OrderImportDetails) => {
					const hasNoValidationErrors = Object.values(details?.validationErrors || {}).length === 0;
					return totalCount + (hasNoValidationErrors ? 1 : 0);
				}, 0); // Start with an initial total count of 0.
			})
		);
	}

	/**
	 * Getter that returns an observable that emits the total count of invalid orders.
	 *
	 * @getter
	 * @returns {Observable<number>}
	 */
	get ordersImportDetailsInvalidCount$(): Observable<number> {
		return this._orderImportDetails$.pipe(
			// Filter out OrderImportDetails objects with empty validation errors.
			filter((detailsArray: Array<OrderImportDetails>) => detailsArray?.length > 0),
			map((detailsArray: Array<OrderImportDetails>) => {
				return detailsArray.reduce((totalCount: number, details: OrderImportDetails) => {
					const hasValidationErrors = Object.values(details?.validationErrors || {}).length > 0;
					return totalCount + (hasValidationErrors ? 1 : 0);
				}, 0); // Start with an initial total count of 0.
			})
		);
	}

	/**
	 * Getter that returns an observable that emits the total payment amount for valid import order details.
	 *
	 * @getter
	 * @returns {Observable<number>} An observable emitting the total payment amount for valid import order details.
	 */
	get ordersImportDetailsValidTotalAmount$(): Observable<number> {
		return this._orderImportDetails$.pipe(
			// Filter out OrderImportDetails objects with empty or null Order properties.
			filter((detailsArray: Array<OrderImportDetails>) => {
				return detailsArray.some((details: OrderImportDetails) => details?.order != null && details?.order?.lineItems?.length > 0);
			}),
			map((detailsArray: Array<OrderImportDetails>) => {
				return detailsArray.reduce((total: number, details: OrderImportDetails) => {
					const hasNoValidationErrors = Object.values(details?.validationErrors || {}).length === 0;
					const orderTotalAmount = (details?.order?.totalAmount != null ? details?.order?.totalAmount : 0);
					return total + (hasNoValidationErrors ? orderTotalAmount : 0);
				}, 0); // Start with an initial total of 0.
			})
		);
	}

	/**
	 * Setter for the import order details that emits the provided array of OrderImportDetails.
	 *
	 * @setter
	 * @param {Array<OrderImportDetails>} details - The array of order import details to set.
	 */
	set orderImportDetails(details: Array<OrderImportDetails>) {
		this._orderImportDetails$.next(details);
	}

	/**
	 * Getter that returns an observable that emits the current import order details.
	 *
	 * @getter
	 * @returns {Observable<Array<OrderImportDetails>>} An observable emitting the current import order details.
	 */
	get orderImportDetails$(): Observable<Array<OrderImportDetails>> {
		return this._orderImportDetails$.asObservable();
	}

	/**
	 * Getter that returns an observable that emits validation errors for the import order details.
	 *
	 * This getter filters the order import details to ensure there are items present, then maps
	 * each detail to extract its validation errors. It returns an observable that emits an array
	 * of validation errors.
	 *
	 * @getter
	 * @returns {Observable<Array<ValidationErrors>>} An observable emitting an array of validation errors for the order import details.
	 */
	get validationErrors$(): Observable<Array<ValidationErrors>> {
		return this.orderImportDetails$.pipe(
			filter((detailsArray: Array<OrderImportDetails>) => detailsArray?.length > 0),
			map((detailsArray: Array<OrderImportDetails>) => {
				return detailsArray.map((details: OrderImportDetails) => {
					return details.validationErrors != null ? details.validationErrors : {};
				});
			})
		);
	}

	/**
	 * Getter that returns an observable that emits the list of orders from the import order details.
	 *
	 * This getter retrieves the current value of the import order details and maps it to extract
	 * the order objects. It returns an observable that emits an array of orders.
	 *
	 * @getter
	 * @returns {Observable<Array<Order>>} An observable emitting an array of orders derived from the order import details.
	 */
	get orders$(): Observable<Array<Order>> {
		const orders: Array<Order> = this._orderImportDetails$?.getValue().map((details: OrderImportDetails) => details.order);
		return of(orders);
	}

	/**
	 * Creates an instance of the OrderImportService.
	 *
	 * @constructor
	 * @param {UtilsService} _utilsService - The service providing utility functions for the application.
	 * @param {AppContextService} _contextService - The service managing the application context.
	 * @param {CountryService} _countryService - The service responsible for managing country-related data.
	 * @param {CustomFieldService} _customFieldService - The service for handling custom field operations.
	 * @param {OrderLineItemService} _lineItemService - The service for managing order line items.
	 * @param {OrderService} _orderService - The service for handling order-related operations.
	 * @param {CompanyService} _companyService - The service for managing company-related data.
	 * @param {UserService} _userService - The service for managing user-related operations.
	 */
	constructor(
		private _utilsService: UtilsService,
		private _contextService: AppContextService,
		private _countryService: CountryService,
		private _customFieldService: CustomFieldService,
		private _lineItemService: OrderLineItemService,
		private _orderService: OrderService,
		private _companyService: CompanyService,
		private _userService: UserService,
	) {
		this.payer = null;
		this.customFields = [];

		this.inputDocument = null;
		this._orderImportDetails$ = new BehaviorSubject<Array<OrderImportDetails>>([]);

		this._contextService.currentContext
			.pipe(filter((context: AppContext) => context !== null))
			.subscribe((ctx: AppContext) => {
				this.ctx = ctx;
				this.payer = new UserContextRef();
				this.payer.userId = ctx.user.id;

				if (ctx.isPersonalContext) {
					this.payer.profile = ctx.user.individual.profile;
				} else {
					this._companyService.getById(ctx.companyMembership.companyId).subscribe((company: Company) => {
						this.payer.profile = company.profile;
					});
				}
			});
	}

	/**
	 * Retrieves order import details based on the import identifier.
	 *
	 * @param {string} importId - The import ID to search for.
	 * @returns {OrderImportDetails} The order import details matching the import ID, or undefined if not found.
	 */
	public getOrderImportDetailsByImportId(importId: string): OrderImportDetails {
		return this._orderImportDetails$?.getValue()?.find((details: OrderImportDetails) => details.importId === importId);
	}

	/**
	 * Updates the import order details with changes and optionally removes an entry by import identifier.
	 *
	 * @param {OrderImportDetails} toUpdate - The details to update or add.
	 * @param {string} [removeImportId] - The import ID of the entry to remove (optional).
	 */
	public updateOrderImportDetails(toUpdate: OrderImportDetails, removeImportId?: string): void {
		if (toUpdate === null && removeImportId == null) return;

		const currentDetails: Array<OrderImportDetails> = this._orderImportDetails$.getValue();
		let updatedDetails: Array<OrderImportDetails> = [];
		if (removeImportId != null) { // Handle removal.
			const removeImportDetails: OrderImportDetails = this.getOrderImportDetailsByImportId(removeImportId);
			if (removeImportDetails != null) { // Remove entry at specific index.
				const index: number = currentDetails.findIndex((detail: OrderImportDetails) => detail.importId === removeImportDetails.importId);
				if (currentDetails.length === 1) updatedDetails = []; // Only one element, assign directly to updatedDetails.
				else updatedDetails = currentDetails.slice(0, index).concat(currentDetails.slice(index + 1));
			}
		} else if (toUpdate) { // Handle update.
			if (currentDetails) {
				const index: number = currentDetails.findIndex((detail: OrderImportDetails) => detail.importId === toUpdate.importId);
				if (index !== -1) { // If the detail with the same ID exists, update it.
					updatedDetails = [...currentDetails.slice(0, index), toUpdate, ...currentDetails.slice(index + 1)];
				} else { // If not, add it to the array.
					updatedDetails = [...currentDetails, toUpdate];
				}
			} else updatedDetails = [toUpdate];
		}

		this.orderImportDetails = updatedDetails;
	}

	/**
	 * Retrieves the import order fields and map them to the Order object.
	 *
	 * @param {OrderImport} importOrder - The imported order to validate.
	 * @returns {Observable<Order>} An observable emitting the retrieved order.
	 */
	public retrieveOrder(importOrder: OrderImport): Observable<Order> {
		console.log(`Fetching the imported order {${importOrder?.title}}.`);

		const order: Order = new Order();

		// Initializes the OrderImportDetails.
		const orderImportDetails: OrderImportDetails = new OrderImportDetails();
		orderImportDetails.importId = importOrder?.id; // Record the unique import ID.
		orderImportDetails.orderImport = importOrder; // Record the source import order.
		this.updateOrderImportDetails(orderImportDetails);

		return of(order).pipe(
			switchMap((order: Order) => {
				const payerCompanyContextId: number | null
					= this._contextService?.currentContext?.value?.isCompanyContext
						? this._contextService?.currentContext?.value?.companyMembership?.companyId
						: null;
				return this._userService.getOrderUserPairs(importOrder.payeeFullName, payerCompanyContextId).pipe(
					// Perform manipulations with the performer.
					switchMap((pairs: Array<OrderUserPair>) => {
						// ETT-526 In case the payee is missing make sure to update order.
						if (! pairs?.length) {
							// Update order with imported order data.
							const modifiedOrder: Order = this._patchOrder(order, importOrder, null);
							if (orderImportDetails && modifiedOrder) {
								orderImportDetails.order = modifiedOrder;
								// @note Make sure to update the modified dates for the import order.
								orderImportDetails.orderImport.startDate = this._utilsService.dateToUTCWithoutTimezone(orderImportDetails?.order?.startDate);
								orderImportDetails.orderImport.finishDate = this._utilsService.dateToUTCWithoutTimezone(orderImportDetails?.order?.finishDate);
								this.updateOrderImportDetails(orderImportDetails); // Record the modified order details.
							}
							return of(modifiedOrder);
						}

						const payees: Array<OrderUserPair> = pairs.filter((pair: OrderUserPair) => pair.recipient.id !== this.ctx.user.id).map((pair: OrderUserPair) => {
							const agentPair: OrderUserPair = Object.assign(new OrderUserPair(), pair);
							agentPair.recipient = Object.assign(new UserPair(), pair.recipient);
							if (pair.agent) agentPair.agent = Object.assign(new UserPair(), pair.agent);
							if (pair.agency) agentPair.agency = Object.assign(new CompanyPair(), pair.agency);
							return agentPair;
						});

						// Match the imported performer with the available performer pairs.
						const selectedPayee: string = this._matchOrderUserPair(importOrder?.payeeFullName, importOrder?.agencyName, payees);
						return this._fetchPayee(selectedPayee, payees, importOrder).pipe(
							// filter(() => selectedPayee != null),
							switchMap((responseCombined: [Array<State>, Array<CustomField>, OrderUserPair]) => {
								// ETT-526 Allow invalid order or missing pair to be processed.
								// if (selectedPayee == null || responseCombined == null) return of(null);
								let payee: OrderUserPair = null;
								if (Array.isArray(responseCombined)) {
									if (responseCombined[0] != null) this.states = responseCombined[0];
									if (responseCombined[1] != null) this.customFields = responseCombined[1];
									if (responseCombined[2] != null) payee = responseCombined[2];
								}

								// Update order with imported order data.
								const modifiedOrder: Order = this._patchOrder(order, importOrder, payee);
								if (orderImportDetails) {
									orderImportDetails.order = modifiedOrder;
									// @note Make sure to update the modified dates for the import order.
									orderImportDetails.orderImport.startDate = this._utilsService.dateToUTCWithoutTimezone(orderImportDetails?.order?.startDate);
									orderImportDetails.orderImport.finishDate = this._utilsService.dateToUTCWithoutTimezone(orderImportDetails?.order?.finishDate);
									this.updateOrderImportDetails(orderImportDetails); // Record the modified order details.
								}
								return of(modifiedOrder);
							})
						);
					})
				);
			})
		);
	}

	/**
	 * Validates an order and returns an array of validation error messages.
	 *
	 * @param {Order} order - The order to validate.
	 * @param {Array<CustomField>} [customFields=[]]
	 * @returns {Object} An object of validation error messages.
	 */
	static validateOrder(order: Order, customFields: Array<CustomField> = []): ValidationErrors {
		console.log(`Validating the imported order {${order?.title}}.`);

		const validationErrors: ValidationErrors = {};
		const isUSA: boolean = CountryService.checkCountryId(order?.countryId, USA_COUNTRY_ID);
		if (order == null || order?.payeeId == null) {
			validationErrors.recipient = `The payee's details aren't valid or their account can't be found. Please check the provided details.`;
			return validationErrors; // @note No need to proceed as the payee is invalid and the form controls depend on it.
		}
		if (! order.vendorLineItems) {
			if (! isUSA && (order?.startDate == null || ! UtilsService.isValidDate(order?.startDate))) {
				validationErrors.startDate = `The start date is required for any payments made to Canada.`;
			}
			if (! isUSA && (order?.finishDate == null || ! UtilsService.isValidDate(order?.finishDate))) {
				validationErrors.endDate = `The end date is required.`;
			}
			if (isUSA && order?.startDate == null || ! UtilsService.isValidDate(order?.startDate)) {
				validationErrors.startDate = `The end date is required for any payments made to US.`;
			}
			if (order?.startDate > order?.finishDate) {
				validationErrors.endDate = `The end date should be later than the start date.`;
			}

			if (order?.isLumpSumCalculation
			&& (order?.rate == null || order?.rate <= 0)) {
				validationErrors.rate = `The lump sum is required.`;
			} else if (! order?.isLumpSumCalculation) {
				if (order?.rate == null || order?.rate <= 0) validationErrors.rate = `The rate is required.`;
				if (order?.quantity == null || order?.quantity <= 0) validationErrors.quantity = `The hours must be provided.`;
			}
		} else {
			// Validate vendor line items, if applicable.
			const isVendorLineItemsInvalid: boolean = order.vendorLineItems.some((vendorLineItem: VendorLineItem) => {
				return vendorLineItem?.description?.length <= 0
					|| vendorLineItem?.rate <= 0
					|| vendorLineItem?.quantity <= 0;
			});
			if (isVendorLineItemsInvalid) validationErrors.vendorLineItems = `The vendor line items aren't valid.`;
		}

		if (! isUSA && order?.isFlatRateCalculation && ! order?.isLumpSumCalculation) {
			validationErrors.rate = `Only the lump sum calculation with the subtract fee option is allowed.`;
		}

		// Validate custom field values to contain necessary value data.
		const invalidCF = customFields
			?.filter((field: CustomField) => field.required)
			// Debugging: Check filtered required fields
			?.map((field: CustomField) => {
				const cfv: CustomFieldValue = order?.customFieldValues?.find(v => v.fieldId === field.id);
				const isValid = !! cfv
					&& field.name === cfv.fieldName
					&& cfv?.values?.length > 0
					&& cfv?.valuePreview?.trim()?.length > 0;
				return { field, isValid };
			})
			?.filter(({ field, isValid }) => ! isValid)
			?.reduce((acc: Record<string, string>, { field, isValid }) => {
				const cfv = order?.customFieldValues?.find(v => v.fieldId === field.id);
				let errorMessage: string = `The ${field.label} doesn't have a value.`;
				if (! cfv) {
					errorMessage = `The ${field.label} is required but missing.`;
				} else if (cfv?.fieldName !== field.name) {
					errorMessage = `${field.label} name doesn't match CustomFieldValue entry (expected: ${field.name}, found: ${cfv.fieldName}).`;
				}

				acc[field.id] = errorMessage;
				return acc;
			}, {});
		if (invalidCF != null && Object.keys(invalidCF)?.length > 0) {
			validationErrors.invalidCF = `The add-on fields are not valid:\n${Object.values(invalidCF).join('\n')}`;
			console.log(validationErrors.invalidCF);
		}

		return validationErrors;
	}

	/**
	 * Validates each of the import orders and updates them with the provided details.
	 */
	public validateOrders() {
		this._orderImportDetails$?.getValue().forEach((details: OrderImportDetails) => {
			if (details.order != null) {
				details.validationErrors = OrderImportService.validateOrder(details.order);
				if (details.order != null) details.order.deferred = (details.validationErrors != null && Object.keys(details.validationErrors)?.length > 0);
				this.updateOrderImportDetails(details);
			}
		});
	}

	/**
	 * Calculates the order quantity based on hours and minutes.
	 *
	 * @param {number} hours - The number of hours.
	 * @param {number} minutes - The number of minutes.
	 * @returns {number} The calculated order quantity.
	 *
	 * @private
	 * @hidden
	 */
	private _calculateOrderQuantity(hours: number, minutes: number): number {
		if (typeof hours !== 'number') { hours = 0; }
		if (typeof minutes !== 'number') { minutes = 0; }

		const seconds = 60;
		const qtyInMinutes = (hours * seconds) + minutes;
		return qtyInMinutes > 0 ? parseFloat((qtyInMinutes / seconds).toFixed(2)) : 0;
	}

	/**
	 * Matches a payee and agency pair based on the provided payee full name and agency name.
	 *
	 * @param {string} payeeFullName - The full name of the payee to match.
	 * @param {string} agencyName - The name of the agency to match.
	 * @param {Array<OrderUserPair>} payees - The array of payee-agent pairs to search for matches.
	 * @returns {string | null} A formatted string representing the matched payee and agency, or null if no match is found.
	 *
	 * @private
	 * @hidden
	 */
	private _matchOrderUserPair(
		payeeFullName: string,
		agencyName: string,
		payees: Array<OrderUserPair>
	): string | null {
		// Prioritize finding a payee with matching agency and client names.
		if (!! agencyName) {
			for (const pair of payees) {
				if (agencyName === pair.agency?.name && payeeFullName === pair.recipient.fullName) {
					return `${payeeFullName} (${agencyName} - ${pair?.defaultTemplate?.title})`;
				}
			}
		}

		// If no agency match is found, return payee with matching client name only.
		for (const pair of payees) {
			if (pair.recipient.fullName === payeeFullName) {
				return `${payeeFullName} (Direct pay - ${pair?.defaultTemplate?.title})`;
			}
		}

		// If no matching payee is found, return null.
		return null;
	}

	/**
	 * Fetches information related to the selected payee from the provided OrderUserPair details.
	 *
	 * @note OrderUserPair array is based on the imported order data.
	 *
	 * @param {string} selectedPayee - The selected payee text to fetch information for.
	 * @param {Array<OrderUserPair>} payees - The array of payee-agent pairs to search for the selected payee.
	 * @param {OrderImport} importOrder - The imported order.
	 * @returns {Observable<[Array<State>, Array<CustomField>]>} An observable emitting an array of states and custom fields related to the selected payee.
	 *
	 * @private
	 * @hidden
	 */
	private _fetchPayee(
		selectedPayee: string,
		payees: Array<OrderUserPair>,
		importOrder: OrderImport
	): Observable<[Array<State>, Array<CustomField>, OrderUserPair]> {
		const payee: OrderUserPair = payees.find((pair: OrderUserPair) => pair.text === selectedPayee);
		if (payee == null) return of(null);

		return forkJoin<[Array<State>, Array<CustomField>, OrderUserPair]>([
			this._fetchStates(payee),
			this._fetchCustomFields(importOrder, payee),
			of(payee)
		]);
	}

	/**
	 * Fetches the list of states based on the given country.
	 *
	 * @param {OrderUserPair} payee - The payee-agent pair to search for the payee profile address.
	 * @returns {Observable<Array<State>>}
	 *
	 * @private
	 * @hidden
	 */
	private _fetchStates(payee: OrderUserPair): Observable<Array<State>> {
		return this._countryService.getStates(payee?.address?.countryId);
	}

	/**
	 * Fetches the list of custom fields for a selected payee.
	 *
	 * @param {OrderImport} importOrder - The imported order.
	 * @param {OrderUserPair} payee - The payee-agent pair to search for the payee profile address.
	 * @returns {Observable<Array<CustomField>>}
	 *
	 * @private
	 * @hidden
	 */
	private _fetchCustomFields(
		importOrder: OrderImport,
		payee: OrderUserPair
	): Observable<Array<CustomField>> {
		this.customFields = []; // Clear additional fields each time method is called.

		const rules = new Map<string, string>();
		rules.set('countries', payee?.address?.countryId?.toString());
		rules.set('states', payee?.address?.stateId?.toString());
		rules.set('pages', 'orders/create/(.*)');
		// @note The agent's fee depends on this rule. Otherwise, you won't be able to hide the custom field in ETT-566.
		rules.set('calc_direction', importOrder?.isFlatRateCalculation ? 'reverse' : 'forward');
		if (payee.defaultTemplate) { // Always use the actual PAYEE's order template (union) - never agent's.
			rules.set('order_templates', payee?.defaultTemplate?.id?.toString());
		}
		// @todo Check if can be removed to not confuse.
		// rules.set('secondary_countries', this.payer?.profile?.address?.countryId?.toString()); // @note This is no longer used, as per comments in ETT-156.
		// const payerSignatory: CustomFieldValue = this.payer?.profile?.customFieldValues?.find(cfValue => cfValue.fieldName === PAYER_SIGNATORY_OPTIONS);
		// if (payerSignatory) rules.set('signatories', payerSignatory?.values?.toString());

		return this._customFieldService.getCustomFields(rules).pipe(
			map((cfs: Array<CustomField>) => {
				// In case country is Canada.
				// ETT-88 -- Expand the dynamic select for Place of Performance with states list.
				let newCustomFields: Array<CustomField> = [];
				if (CountryService.checkCountryId(payee?.address?.countryId, CANADA_COUNTRY_ID)) {
					// eslint-disable-next-line array-callback-return
					cfs.map((customField: CustomField) => {
						// If custom field is typeof Dynamic Select and is a Place of Performance.
						if (customField.type.alias === 'dynamic_select'
						&& customField.name === PLACE_OF_PERFORMANCE
						&& this.states != null) {
							// eslint-disable-next-line array-callback-return
							this.states.map((state: State) => {
								// Create a new option for the select pull-down.
								const chosen: CustomFieldChoice = new CustomFieldChoice();
								chosen.id    = state.id.toString();
								chosen.name  = state.name + ' (' + state.taxPercent + '%)';
								chosen.group = false;
								customField.choiceList.push(chosen);
							});
						}
						newCustomFields.push(customField);
					});
				} else { // For any other countries.
					newCustomFields = cfs;
				}

				newCustomFields.forEach((customField: CustomField) => {
					if (customField.name === PAYMENT_SIGNATORY) {
						const payerSignatory: CustomFieldValue = this.payer?.profile?.customFieldValues?.find(cfValue => cfValue.fieldName === PAYER_SIGNATORY_OPTIONS);
						if (payerSignatory) {
							customField.defaultValue = payerSignatory?.values?.toString();
							customField.displayValue = payerSignatory?.valuePreview?.toString();
						}
					}
					const existingCFIndex: number = this.customFields.findIndex((existingCustomField: CustomField) => existingCustomField.id === customField.id);
					if (existingCFIndex !== -1) this.customFields[existingCFIndex] = customField;
					else this.customFields.push(customField);
				});

				// Sort custom fields according to the display order.
				this.customFields = this._customFieldService.sortCustomFields(this.customFields, 'displaySort');

				return this.customFields;
			})
		);
	}

	/**
	 * Updates custom field values according to import order details.
	 *
	 * @param {OrderImport} importOrder - The imported order.
	 * @returns {Array<CustomFieldValue>}
	 *
	 * @private
	 * @hidden
	 */
	private _patchCustomFieldValue(importOrder: OrderImport): Array<CustomFieldValue> {
		const cfValues: Array<CustomFieldValue> = [];
		if (importOrder?.vendorServicePerformed?.length > 0) return cfValues;
		if (this.customFields?.length > 0) {
			this.customFields.forEach((customField: CustomField) => {
				const cfv = new CustomFieldValue();
				cfv.fieldName = customField.name;
				cfv.fieldId = customField.id;
				cfv.fieldLabel = customField.label;

				if (Object.prototype.hasOwnProperty.call(importOrder?.customFieldValues, customField.name)) {
					let cfValue = null;
					if (customField?.type?.alias === 'checkbox') {
						cfValue = importOrder?.customFieldValues[customField.name];
					} else {
						cfValue = (importOrder?.customFieldValues[customField.name] === 'true' || importOrder?.customFieldValues[customField.name] === 'false'
							? importOrder?.customFieldValues[customField.name] === 'true'
							: importOrder?.customFieldValues[customField.name]);
					}

					// @note Payers specify the Canadian custom fields as plain values. They must be programmatically matched.
					if (customField.name === PLACE_OF_PERFORMANCE) {
						const state: State = this.states?.find((state: State) => state.name.toUpperCase().indexOf(cfValue?.toString()?.toUpperCase()) !== -1);
						if (state) {
							cfValue = state.id;
							cfv.values = cfValue;
							cfv.valuePreview = state.name;
						}
					} else if ([ACTRA_NATURE_OF_PRODUCTION, ACTRA_PERFORMANCE_CATEGORY].includes(customField.name)) {
						if (customField?.choiceList?.length > 0) {
							const matchedOption: CustomFieldChoice = this.findCustomFieldChoice(customField.choiceList, cfValue);
							if (matchedOption) {
								cfValue = matchedOption.id;
								cfv.values = cfValue;
								cfv.valuePreview = matchedOption.name;
							}
						}
					} else if (customField?.type?.alias === 'checkbox') {
						cfv.values = cfValue;
						cfv.valuePreview = cfValue;
					}
				} else {
					cfv.values = customField.defaultValue;
					cfv.valuePreview = customField.displayValue;
				}

				cfValues.push(cfv);
			});
		}
		return cfValues;
	}

	/**
	 * Finds a custom field choice within a source choices list recursively.
	 *
	 * @param {Array<CustomFieldChoice>} choiceList - The list of custom field choices to search.
	 * @param {string} cfValue - The value to search for within the choices.
	 * @returns {CustomFieldChoice | undefined} The found custom field choice or undefined if not found.
	 */
	public findCustomFieldChoice(choiceList: Array<CustomFieldChoice>, cfValue: string): CustomFieldChoice | undefined {
		for (const cfOptions of choiceList) {
			if (cfOptions?.name?.toUpperCase().indexOf(cfValue?.toUpperCase()) !== -1) return cfOptions; // Found on the top level.

			// Recursive search within children.
			const childResult: CustomFieldChoice = this.findCustomFieldChoice(cfOptions?.children || [], cfValue);
			if (childResult) return childResult; // Found within children.
		}

		return undefined; // Not found.
	};

	/**
	 * Maps the order line items with the imported order line items data.
	 *
	 * @param {Order} order - The order to preview line item values.
	 * @returns {Observable<Array<OrderLineItemGroup>>}
	 */
	public patchOrderLineItems(order: Order): Observable<Order> {
		if (order == null) return null;
		order.lineItems = [];
		return this._lineItemService.getPreviewItems(order).pipe(
			switchMap((updatedOrder: Order) => {
				const orderLineItemValues: Array<OrderLineItemGroup> = this._lineItemService.groupItems(updatedOrder.lineItems);
				updatedOrder.lineItems = this._lineItemService.ungroupItems(orderLineItemValues);
				return of(updatedOrder);
			})
		);
	}

	/**
	 * Maps the order with the imported order data.
	 *
	 * @param {Order} order - The original order to be updated.
	 * @param {OrderImport} importOrder - The imported order containing new data.
	 * @param {OrderUserPair} payee - The payee-agent pair to search for the payee profile address.
	 * @returns {Order | null} The updated order, or null if either input is null.
	 *
	 * @private
	 * @hidden
	 */
	private _patchOrder(
		order: Order,
		importOrder: OrderImport,
		payee: OrderUserPair
	): Order {
		if (order == null || importOrder == null) return null;
		console.log(`Mapping order with the imported order {${importOrder?.title}}.`);

		// if (order?.importId == null) order.importId = importOrder?.id != null ? importOrder?.id : this._utilsService.uuid();
		order.title = importOrder?.title;
		order.memoDisplayed = importOrder?.memoDisplayed;
		order.startDate = this._utilsService.dateToUTCWithoutTimezone(importOrder?.startDate?.toString());
		order.finishDate = payee?.address?.countryId == null || CountryService.checkCountryId(payee?.address?.countryId, USA_COUNTRY_ID)
			? order.startDate
			: this._utilsService.dateToUTCWithoutTimezone(importOrder?.finishDate?.toString());

		// Set the payer.
		order.payerId = this.ctx?.user?.id;
		order.payerCompanyId = this.ctx?.companyMembership?.companyId;

		const payerCFVs: Array<CustomFieldValue>
			= this.ctx?.companyMembership?.companyId
				? (this.ctx?.user?.company as Company)?.profile?.customFieldValues
				: this.ctx?.user?.individual?.profile?.customFieldValues;
		const payerSignatory: CustomFieldValue = payerCFVs.find(
			(cfValue: CustomFieldValue) => cfValue.fieldName === PAYER_SIGNATORY_OPTIONS);
		if (payerSignatory) order.payerSignatoryId = payerSignatory?.values?.toString();

		// If the existing recipient was chosen from the drop-down.
		if (payee) {
			// Set the agent.
			if (payee?.agency) order.agencyId = payee?.agency?.id;
			if (payee?.agent) order.agentId = payee?.agent?.id;
			// Set the payee.
			order.payeeId = payee?.recipient?.id;
			// @note Before implementing the ETT-526, we always used the actual PAYEE's order template (union).
			order.templateId = importOrder?.vendorServicePerformed?.length > 0 ? NON_UNION_ORDER_TEMPLATE_ID : payee?.defaultTemplate?.id;
			order.countryId = payee?.defaultTemplate?.country?.id;
			order.countryCurrencyCode   = payee?.defaultTemplate?.country?.currencyCode;
			order.countryCurrencySymbol = payee?.defaultTemplate?.country?.currencySymbol;
			order.stateId = payee?.address?.stateId;
		} else {
			const payee = new OrderUser();
			payee.firstName = importOrder?.payeeFirstName;
			payee.middleName = importOrder?.payeeMiddleName;
			payee.lastName = importOrder?.payeeLastName;
			order.payeeDetails = payee;
		}

		order.isFlatRateCalculation = importOrder?.isFlatRateCalculation;

		// @todo Switch to signatory validation if a more universal solution is required.
		if (/* this._orderService.isACX(this.payer.profile, payee)
		||  */importOrder?.isFlatRateCalculation !== false
		&& this._orderService.isBrilliance(this._contextService.currentContext.value?.companyMembership?.companyName)) {
			order.isFlatRateCalculation = true;
		}

		order.isLumpSumCalculation = (importOrder?.hours == null);
		order.rate = importOrder?.rate;
		order.quantity = this._calculateOrderQuantity(importOrder?.hours, importOrder?.minutes);

		// Payment additional details.
		if (importOrder?.vendorServicePerformed?.length > 0) {
			// Make sure to convert the source hours and minutes into the quantity record.
			const recalculatedQty: number = this._utilsService.calculateHours(importOrder?.hours, importOrder?.minutes);
			const vendorLineItem: VendorLineItem = new VendorLineItem();
			vendorLineItem.description = importOrder?.vendorServicePerformed;
			vendorLineItem.quantity = recalculatedQty;
			vendorLineItem.rate = importOrder?.rate;

			// @note At the moment, only one vendor line item can be imported.
			order.vendorLineItems = [];
			order.vendorLineItems.push(vendorLineItem);
		} else order.vendorLineItems = [];

		if (this.customFields?.length > 0) {
			order.customFieldValues = this._patchCustomFieldValue(importOrder);
		}

		return order;
	}

	/**
	 * Updates the import order with data from the existing order.
	 *
	 * @note Invoked when the draft, or import orders are modified.
	 *
	 * @param {Order} order - The existing order to extract data from.
	 * @param {OrderImport} importOrder - The import order to update.
	 * @returns {OrderImport} The updated import order.
	 */
	public patchOrderImport(order: Order, importOrder: OrderImport): OrderImport {
		if (order == null || importOrder == null) return null;
		console.log(`Mapping imported order with the order {${importOrder?.title || order?.title}}.`);

		const isUSA: boolean = CountryService.checkCountryId(order?.countryId, USA_COUNTRY_ID);

		if (order.title) importOrder.title = order.title;
		if (order.memoDisplayed) importOrder.memoDisplayed = order.memoDisplayed;

		if (! isUSA && order?.isFlatRateCalculation && ! order?.isLumpSumCalculation) {
			importOrder.isFlatRateCalculation = order.isFlatRateCalculation;
			importOrder.isLumpSumCalculation = true;
			importOrder.rate = order.rate;
		} else {
			if (order.isFlatRateCalculation) importOrder.isFlatRateCalculation = order.isFlatRateCalculation;
			if (order.isLumpSumCalculation) importOrder.isLumpSumCalculation = order.isLumpSumCalculation;
			if (! order.isLumpSumCalculation) {
				importOrder.rate = order.rate;
				importOrder.hours = order.quantity;
				importOrder.minutes = 0;
			} else {
				importOrder.rate = order.rate;
			}
		}

		// When the order is for a vendor, the original quantity and rate have to be updated.
		if (order?.vendorLineItems != null && order?.vendorLineItems?.length > 0) {
			importOrder.rate = order.vendorLineItemsAmountTotal;
			importOrder.hours = 1;
			importOrder.minutes = 0;
		}

		if (UtilsService.isValidDate(order.finishDate)) {
			importOrder.finishDate = new Date(order.finishDate);
		}
		if (UtilsService.isValidDate(order.startDate)) {
			importOrder.startDate = new Date(order.startDate);
			if (! UtilsService.isValidDate(order.finishDate)) {
				importOrder.finishDate = importOrder.startDate;
			}
		}

		if (order?.customFieldValues?.length > 0) {
			importOrder.customFieldValues = [];
			order.customFieldValues?.forEach((cfValue: CustomFieldValue) => {
				importOrder.customFieldValues[cfValue.fieldName] = (cfValue.values === 'true' || cfValue.values === 'false')
					? cfValue.values === 'true'
					: cfValue.values;
			});
		}

		if (order?.payeeDetails != null) {
			importOrder.payeeFirstName = order?.payeeDetails?.firstName;
			importOrder.payeeMiddleName = order?.payeeDetails?.middleName;
			importOrder.payeeLastName = order?.payeeDetails?.lastName;
			importOrder.payeeFullName = importOrder.payeeMiddleName
				? `${importOrder.payeeFirstName} ${importOrder.payeeMiddleName} ${importOrder.payeeLastName}`
				: `${importOrder.payeeFirstName} ${importOrder.payeeLastName}`;
			importOrder.agencyName = (order?.agentDetails?.company ? order?.agentDetails?.company : null);
		}

		// Set the payer, if it's missing.
		if (order?.payerId == null) {
			order.payerId = this.ctx?.user?.id;
			if (order?.payerCompanyId == null && this.ctx?.companyMembership?.companyId != null) {
				order.payerCompanyId = this.ctx?.companyMembership?.companyId;
			}
		}

		return importOrder;
	}
}
